import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Button,
  Chip,
  Collapse,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import {
  AutoAwesome,
  Close,
  Remove,
  Send,
  Code,
  KeyboardArrowDown,
  OpenInFull,
  CloseFullscreen,
  AddCommentOutlined,
  History,
  ArrowBack,
  DeleteOutline,
  ChatBubbleOutline,
  Bolt,
  CheckCircleOutline,
  ErrorOutline,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useAssistant } from '../../contexts/AssistantContext';
import { AssistantMessage, AssistantPart } from '../../types/assistant';
import { autopilotRegistry } from '../../autopilot/registry';

const SUGGESTIONS = [
  'Top RR batters by total value in the powerplay',
  'Best value spinners under 100 balls',
  'Compare Jaiswal vs pace across phases',
];

const PAGE_LABELS: Record<string, string> = {
  lineups: 'Lineups',
  trade: 'Trade Machine',
  playground: 'Lineup Playground',
};

const AUTOPILOT_SUGGESTIONS: Record<string, string[]> = {
  lineups: [
    'Generate 5 lineups for RR',
    'Rank all 10 teams',
    'Rank the league on 5 lineups and save it',
  ],
  trade: [
    'Which players could RR target from DC for the top order?',
    'Trade Jaiswal to DC for Starc and Duckett — 5 lineups',
    'Trade Hetmyer to MI for Hardik and to KKR for Rinku, 5 lineups',
  ],
  playground: [
    'Build the best RR XI and evaluate it',
    'Find the top unsigned player and try him at 4, 5 and 6',
    'Who bowls the death overs in this lineup?',
  ],
};

const PINK_GRADIENT = 'linear-gradient(135deg, #E74C91 0%, #C13B76 100%)';

// Round long floats for display; leave ids/text/integers as-is.
const fmtCell = (v: string | number | null): string => {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  if (s === '') return '—';
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s);
    if (!Number.isNaN(n)) return String(Number(n.toFixed(3)));
  }
  return s;
};

// ---- Part renderers -------------------------------------------------------

// Minimal markdown: **bold**, `code`, ### headings, and - / * bullet lists.
const renderInline = (text: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(
        <Box
          component="code"
          key={key++}
          sx={{ px: 0.4, py: 0.1, bgcolor: 'grey.100', borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.76rem' }}
        >
          {tok.slice(1, -1)}
        </Box>
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const splitRow = (line: string): string[] => {
  if (line.includes('\t')) return line.split('\t').map((s) => s.trim());
  const cells = line.split('|').map((s) => s.trim());
  if (cells.length && cells[0] === '') cells.shift();
  if (cells.length && cells[cells.length - 1] === '') cells.pop();
  return cells;
};
const isSepRow = (line: string) => line.replace(/[\s|:-]/g, '') === '' && line.includes('-');
const isTableRow = (line: string) =>
  !isSepRow(line) && (line.includes('\t') || line.split('|').length >= 3);

const TableBlock: React.FC<{ rows: string[] }> = ({ rows }) => {
  const parsed = rows.map(splitRow);
  const header = parsed[0];
  const body = parsed.slice(1);
  return (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: '0.78rem' } }}>
        <TableHead>
          <TableRow>
            {header.map((c, i) => (
              <TableCell key={i} sx={{ fontWeight: 700, color: 'text.secondary' }}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {body.map((r, ri) => (
            <TableRow key={ri}>
              {r.map((c, ci) => (
                <TableCell key={ci} sx={{ fontWeight: ci === 0 ? 600 : 400 }}>{fmtCell(c)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const TextPart: React.FC<{ text: string }> = ({ text }) => {
  const blocks: React.ReactNode[] = [];
  let listItems: string[] = [];
  let tableRows: string[] = [];
  let key = 0;
  const flushList = () => {
    if (listItems.length) {
      blocks.push(
        <Box component="ul" key={`ul-${key++}`} sx={{ pl: 2.5, my: 0.5 }}>
          {listItems.map((it, i) => (
            <Box component="li" key={i} sx={{ fontSize: '0.85rem', lineHeight: 1.5, mb: 0.25 }}>
              {renderInline(it)}
            </Box>
          ))}
        </Box>
      );
      listItems = [];
    }
  };
  const flushTable = () => {
    if (tableRows.length) {
      blocks.push(<TableBlock key={`tbl-${key++}`} rows={tableRows} />);
      tableRows = [];
    }
  };
  text.split('\n').forEach((raw) => {
    const t = raw.trim();
    if (!t) {
      flushList();
      flushTable();
      return;
    }
    if (isSepRow(t)) return; // markdown |---|---| separator
    if (isTableRow(t)) {
      flushList();
      tableRows.push(t);
      return;
    }
    flushTable();
    if (/^#{1,6}\s/.test(t)) {
      flushList();
      blocks.push(
        <Typography key={`h-${key++}`} sx={{ fontWeight: 700, fontSize: '0.86rem', mt: 0.75, mb: 0.25 }}>
          {renderInline(t.replace(/^#{1,6}\s+/, ''))}
        </Typography>
      );
    } else if (/^[-*]\s/.test(t)) {
      listItems.push(t.replace(/^[-*]\s+/, ''));
    } else {
      flushList();
      blocks.push(
        <Typography key={`p-${key++}`} variant="body2" sx={{ lineHeight: 1.5, mb: 0.5 }}>
          {renderInline(t)}
        </Typography>
      );
    }
  });
  flushList();
  flushTable();
  return <Box>{blocks}</Box>;
};

const TablePart: React.FC<{ columns: string[]; rows: Array<Array<string | number | null>> }> = ({
  columns,
  rows,
}) => (
  <Box sx={{ overflowX: 'auto', my: 1 }}>
    <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: '0.78rem' } }}>
      <TableHead>
        <TableRow>
          {columns.map((c, i) => (
            <TableCell key={i} sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {c}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r, ri) => (
          <TableRow key={ri}>
            {r.map((cell, ci) => (
              <TableCell key={ci} sx={{ fontWeight: ci === 0 ? 600 : 400 }}>
                {fmtCell(cell)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Box>
);

const ChartPart: React.FC<{ title?: string; data: Array<{ label: string; value: number }> }> = ({
  title,
  data,
}) => {
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 0.0001);
  return (
    <Box sx={{ my: 1 }}>
      {title && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
        {data.map((d, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 92, fontSize: '0.72rem', color: 'text.secondary', flexShrink: 0 }}>
              {d.label}
            </Box>
            <Box sx={{ flex: 1, height: 8, bgcolor: 'grey.100', borderRadius: 999 }}>
              <Box
                sx={{
                  height: 8,
                  borderRadius: 999,
                  width: `${Math.max(6, (Math.abs(d.value) / max) * 100)}%`,
                  background: PINK_GRADIENT,
                }}
              />
            </Box>
            <Box sx={{ width: 40, textAlign: 'right', fontSize: '0.72rem', fontWeight: 600 }}>
              {d.value.toFixed(3)}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const SqlPart: React.FC<{ sql: string }> = ({ sql }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box sx={{ mt: 1 }}>
      <Chip
        icon={<Code sx={{ fontSize: 15 }} />}
        label={expanded ? 'Hide SQL' : 'View SQL'}
        size="small"
        onClick={() => setExpanded((e) => !e)}
        sx={{
          height: 24,
          fontSize: '0.72rem',
          fontWeight: 600,
          color: 'primary.dark',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          '& .MuiChip-icon': { color: 'primary.dark' },
        }}
      />
      <Collapse in={expanded}>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1.25,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
            m: 0,
          }}
        >
          {sql}
        </Box>
      </Collapse>
    </Box>
  );
};

// Follow-up questions rendered as clickable chips at the bottom of a reply.
const SuggestionsPart: React.FC<{ items: string[]; title?: string }> = ({ items, title }) => {
  const { sendMessage, sending } = useAssistant();
  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.75, letterSpacing: 0.2 }}
      >
        {title || 'Ask a follow-up'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
        {items.map((q, i) => (
          <Chip
            key={i}
            label={q}
            size="small"
            variant="outlined"
            onClick={() => !sending && sendMessage(q)}
            disabled={sending}
            sx={{
              height: 'auto',
              maxWidth: '100%',
              cursor: 'pointer',
              color: 'primary.dark',
              borderColor: (t) => alpha(t.palette.primary.main, 0.4),
              bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                fontSize: '0.74rem',
                lineHeight: 1.35,
                py: 0.6,
                px: 0.25,
              },
              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.12) },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Auto-pilot: an action the agent performed on the page (audit-trail row).
const ActionPart: React.FC<{ label: string; status?: 'done' | 'failed' }> = ({ label, status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.2 }}>
    {status === 'failed' ? (
      <ErrorOutline sx={{ fontSize: 15, color: 'error.main' }} />
    ) : (
      <CheckCircleOutline sx={{ fontSize: 15, color: 'success.main' }} />
    )}
    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
      {label}
      {status === 'failed' ? ' — failed' : ''}
    </Typography>
  </Box>
);

// Auto-pilot: a gated action awaiting explicit user approval (e.g. save rankings).
const ConfirmPart: React.FC<{
  messageId: string;
  index: number;
  part: Extract<AssistantPart, { type: 'confirm' }>;
}> = ({ messageId, index, part }) => {
  const { resolveConfirm } = useAssistant();
  const resolved = part.resolved;
  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: (t) => alpha(t.palette.warning.main, 0.45),
        bgcolor: (t) => alpha(t.palette.warning.main, 0.07),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
        <WarningAmberOutlined sx={{ fontSize: 17, color: 'warning.main', mt: 0.2 }} />
        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.45 }}>{part.message}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {resolved ? (
          <Chip
            size="small"
            label={resolved === 'confirmed' ? 'Confirmed' : 'Cancelled'}
            sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
            color={resolved === 'confirmed' ? 'success' : 'default'}
            variant="outlined"
          />
        ) : (
          <>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => resolveConfirm(messageId, index, true)}
              sx={{ fontSize: '0.72rem', py: 0.25, boxShadow: 'none' }}
            >
              Confirm
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={() => resolveConfirm(messageId, index, false)}
              sx={{ fontSize: '0.72rem', py: 0.25, color: 'text.secondary', borderColor: 'divider' }}
            >
              Cancel
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

const PartRenderer: React.FC<{ part: AssistantPart; messageId: string; index: number }> = ({
  part,
  messageId,
  index,
}) => {
  switch (part.type) {
    case 'text':
      return <TextPart text={part.text} />;
    case 'table':
      return <TablePart columns={part.columns} rows={part.rows} />;
    case 'chart':
      return <ChartPart title={part.title} data={part.data} />;
    case 'sql':
      return <SqlPart sql={part.sql} />;
    case 'suggestions':
      return <SuggestionsPart items={part.items} title={part.title} />;
    case 'ui_action':
      return <ActionPart label={part.label} status={part.status} />;
    case 'confirm':
      return <ConfirmPart messageId={messageId} index={index} part={part} />;
    default:
      return null;
  }
};

// ---- Message row ----------------------------------------------------------

// While waiting, stage through one-word status labels so the user sees what's
// happening. The reply arrives in a single request (no streaming), so the
// stages are time-based: quick early steps, longer later ones.
const THINKING_STEPS: Record<string, string[]> = {
  ask: ['Thinking…', 'Querying…', 'Analysing…', 'Summarising…'],
  autopilot: ['Thinking…', 'Planning…', 'Simulating…', 'Evaluating…', 'Finalising…'],
};
const THINKING_DELAYS_MS = [2200, 3500, 5000, 9000];

const TypingDots: React.FC = () => {
  const { mode } = useAssistant();
  const steps = THINKING_STEPS[mode] || THINKING_STEPS.ask;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
    let i = 0;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const advance = () => {
      if (cancelled || i >= steps.length - 1) return;
      timer = setTimeout(() => {
        i += 1;
        setIdx(i);
        advance();
      }, THINKING_DELAYS_MS[Math.min(i, THINKING_DELAYS_MS.length - 1)]);
    };
    advance();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5 }}>
      <Box sx={{ display: 'flex', gap: 0.6 }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: 'sqlab-bounce 1.2s infinite ease-in-out',
              animationDelay: `${i * 0.15}s`,
              '@keyframes sqlab-bounce': {
                '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.5 },
                '40%': { transform: 'scale(1)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
      <Fade in appear key={steps[idx]}>
        <Typography sx={{ fontSize: '0.76rem', color: 'text.secondary', fontWeight: 500 }}>
          {steps[idx]}
        </Typography>
      </Fade>
    </Box>
  );
};

const MessageRow: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  const isUser = message.role === 'user';
  if (isUser) {
    const text = message.parts.find((p) => p.type === 'text');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Box
          sx={{
            maxWidth: '85%',
            px: 1.5,
            py: 1,
            borderRadius: '16px',
            borderTopRightRadius: 4,
            background: PINK_GRADIENT,
            color: 'white',
            fontSize: '0.85rem',
            lineHeight: 1.45,
          }}
        >
          {text && text.type === 'text' ? text.text : ''}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Avatar
        sx={{
          width: 26,
          height: 26,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
          color: 'primary.main',
          flexShrink: 0,
        }}
      >
        <AutoAwesome sx={{ fontSize: 15 }} />
      </Avatar>
      <Fade in>
        <Box
          sx={{
            maxWidth: '90%',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: message.error ? 'error.light' : 'divider',
            borderRadius: '16px',
            borderTopLeftRadius: 4,
          }}
        >
          {message.pending ? (
            <TypingDots />
          ) : (
            message.parts.map((p, i) => (
              <Box key={i} sx={{ '&:not(:last-child)': { mb: 0.5 } }}>
                <PartRenderer part={p} messageId={message.id} index={i} />
              </Box>
            ))
          )}
        </Box>
      </Fade>
    </Box>
  );
};

// ---- Widget ---------------------------------------------------------------

// ---- History panel --------------------------------------------------------

const relTime = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
};

const HistoryPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { conversations, activeId, openConversation, deleteConversation, newConversation } = useAssistant();
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'grey.50', minHeight: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <IconButton size="small" onClick={onClose} aria-label="Back to chat">
          <ArrowBack fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', flex: 1 }}>Chat history</Typography>
        <Chip
          icon={<AddCommentOutlined sx={{ fontSize: 15 }} />}
          label="New chat"
          size="small"
          onClick={() => {
            newConversation();
            onClose();
          }}
          sx={{
            fontWeight: 600,
            color: 'primary.dark',
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            '& .MuiChip-icon': { color: 'primary.dark' },
            cursor: 'pointer',
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {conversations.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 5, px: 2 }}>
            <ChatBubbleOutline sx={{ fontSize: 30, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              No saved chats yet.
            </Typography>
          </Box>
        ) : (
          conversations.map((c) => (
            <Box
              key={c.id}
              onClick={() => {
                openConversation(c.id);
                onClose();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                mb: 0.5,
                borderRadius: 2,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: c.id === activeId ? 'primary.main' : 'divider',
                bgcolor: c.id === activeId ? (t) => alpha(t.palette.primary.main, 0.06) : 'background.paper',
                '&:hover': { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                  {c.title}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                  {relTime(c.updatedAt)} · {c.messageCount} messages
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Delete chat"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteConversation(c.id);
                }}
                sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutline sx={{ fontSize: 17 }} />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

// ---- Widget ---------------------------------------------------------------

const AssistantWidget: React.FC = () => {
  const {
    open,
    messages,
    sending,
    conversations,
    mode,
    setMode,
    openAssistant,
    closeAssistant,
    sendMessage,
    newConversation,
  } = useAssistant();
  const [input, setInput] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  // Page-awareness: which controllable page is mounted right now (null = none).
  const activePage = useSyncExternalStore(
    autopilotRegistry.subscribe,
    () => autopilotRegistry.getActive()?.page ?? null
  );

  // Land on the chat (not history) whenever the panel is reopened.
  useEffect(() => {
    if (!open) setHistoryOpen(false);
  }, [open]);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Draggable position (null = default bottom-right anchor) + expand toggle.
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const clampPos = (p: { x: number; y: number }) => {
    const el = panelRef.current;
    const w = el?.offsetWidth ?? 396;
    const h = el?.offsetHeight ?? 620;
    const maxX = Math.max(8, window.innerWidth - w - 8);
    const maxY = Math.max(8, window.innerHeight - h - 8);
    return { x: Math.min(Math.max(8, p.x), maxX), y: Math.min(Math.max(8, p.y), maxY) };
  };

  const onDragStart = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return; // let buttons work
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top };
    setPos({ x: rect.left, y: rect.top }); // switch from right/bottom to left/top anchor in place
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    document.body.style.userSelect = 'none';
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPos(clampPos({ x: d.origX + (e.clientX - d.startX), y: d.origY + (e.clientY - d.startY) }));
    };
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragging]);

  // Keep the panel on-screen when it grows/shrinks or the window resizes.
  useEffect(() => {
    const reclamp = () => setPos((p) => (p ? clampPos(p) : p));
    const id = requestAnimationFrame(reclamp);
    window.addEventListener('resize', reclamp);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', reclamp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  useEffect(() => {
    if (open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  const submit = () => {
    if (!input.trim() || sending) return;
    sendMessage(input);
    setInput('');
  };

  if (!open) {
    return (
      <Box
        onClick={openAssistant}
        role="button"
        aria-label="Open SquadLab Assistant"
        sx={{
          position: 'fixed',
          right: 28,
          bottom: 28,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 1.25,
          pr: 2,
          py: 1.25,
          borderRadius: 999,
          color: 'white',
          background: PINK_GRADIENT,
          boxShadow: '0 10px 26px rgba(231,76,145,0.42)',
          cursor: 'pointer',
          transition: 'transform 0.15s ease',
          '&:hover': { transform: 'translateY(-2px)' },
        }}
      >
        <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(255,255,255,0.22)' }}>
          <AutoAwesome sx={{ fontSize: 17, color: 'white' }} />
        </Avatar>
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Ask SquadLab</Typography>
      </Box>
    );
  }

  return (
    <Paper
      ref={panelRef}
      elevation={0}
      sx={{
        position: 'fixed',
        ...(pos ? { left: pos.x, top: pos.y } : { right: 28, bottom: 28 }),
        zIndex: 1300,
        width: expanded ? 'min(900px, calc(100vw - 40px))' : { xs: 'calc(100vw - 32px)', sm: 396 },
        height: expanded ? 'min(920px, calc(100vh - 40px))' : { xs: 'calc(100vh - 120px)', sm: 620 },
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '22px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 24px 70px rgba(30,42,90,0.28)',
        transition: dragging ? 'none' : 'width 0.2s ease, height 0.2s ease',
      }}
    >
      {/* Header (drag handle) */}
      <Box
        onPointerDown={onDragStart}
        sx={{
          background: PINK_GRADIENT,
          color: 'white',
          px: 1.75,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: dragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'white', p: 0.5 }}>
          <Box
            component="img"
            src="/static/metalogo/rr-crest.png"
            alt="RR"
            sx={{ height: '100%', width: 'auto', objectFit: 'contain' }}
          />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1 }}>
            SquadLab Assistant
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, minWidth: 0 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#41d18f', flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '0.7rem', opacity: 0.92 }}>
              Online
            </Typography>
          </Box>
        </Box>
        {conversations.length > 0 && (
          <IconButton
            size="small"
            onClick={() => setHistoryOpen((v) => !v)}
            data-no-drag
            sx={{ color: 'white', bgcolor: historyOpen ? 'rgba(255,255,255,0.18)' : 'transparent' }}
            aria-label="Chat history"
            title="Chat history"
          >
            <History sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        {messages.length > 0 ? (
          <IconButton
            size="small"
            onClick={() => {
              newConversation();
              setHistoryOpen(false);
            }}
            data-no-drag
            sx={{ color: 'white' }}
            aria-label="New chat"
            title="New chat"
          >
            <AddCommentOutlined sx={{ fontSize: 17 }} />
          </IconButton>
        ) : conversations.length === 0 ? (
          <Chip
            label="Projections"
            size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.22)', color: 'white' }}
          />
        ) : null}
        <IconButton
          size="small"
          onClick={() => setExpanded((v) => !v)}
          data-no-drag
          sx={{ color: 'white' }}
          aria-label={expanded ? 'Shrink chat' : 'Expand chat'}
        >
          {expanded ? <CloseFullscreen sx={{ fontSize: 17 }} /> : <OpenInFull sx={{ fontSize: 17 }} />}
        </IconButton>
        <IconButton size="small" onClick={closeAssistant} data-no-drag sx={{ color: 'white' }} aria-label="Minimize">
          <KeyboardArrowDown fontSize="small" />
        </IconButton>
      </Box>

      {/* Mode switch: Ask (read-only Q&A) vs Auto-pilot (controls the page) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            borderRadius: 999,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setMode('ask')}
            role="button"
            aria-label="Ask mode"
            sx={{
              px: 1.25,
              py: 0.4,
              fontSize: '0.74rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: mode === 'ask' ? 'white' : 'text.secondary',
              background: mode === 'ask' ? PINK_GRADIENT : 'transparent',
            }}
          >
            <AutoAwesome sx={{ fontSize: 13 }} /> Ask
          </Box>
          <Box
            onClick={() => setMode('autopilot')}
            role="button"
            aria-label="Auto-pilot mode"
            sx={{
              px: 1.25,
              py: 0.4,
              fontSize: '0.74rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: mode === 'autopilot' ? 'white' : 'text.secondary',
              background: mode === 'autopilot' ? 'linear-gradient(135deg, #1E2A5A 0%, #34437E 100%)' : 'transparent',
            }}
          >
            <Bolt sx={{ fontSize: 14 }} /> Auto-pilot
          </Box>
        </Box>
        {mode === 'autopilot' && (
          <Chip
            size="small"
            label={
              activePage
                ? `Controlling: ${PAGE_LABELS[activePage] || activePage}`
                : 'Open Lineups or Trade Machine'
            }
            sx={{
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 600,
              color: activePage ? 'success.dark' : 'warning.dark',
              bgcolor: (t) => alpha(activePage ? t.palette.success.main : t.palette.warning.main, 0.12),
            }}
          />
        )}
      </Box>

      {historyOpen ? (
        <HistoryPanel onClose={() => setHistoryOpen(false)} />
      ) : (
        <>
      {/* Body */}
      <Box ref={bodyRef} sx={{ flex: 1, overflowY: 'auto', p: 1.75, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.length === 0 ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Avatar sx={{ width: 26, height: 26, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', flexShrink: 0 }}>
              <AutoAwesome sx={{ fontSize: 15 }} />
            </Avatar>
            <Box sx={{ maxWidth: '90%', px: 1.5, py: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: '16px', borderTopLeftRadius: 4 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {mode === 'autopilot'
                  ? activePage
                    ? `Auto-pilot is on — I can control the ${PAGE_LABELS[activePage] || activePage} page for you. Try:`
                    : 'Auto-pilot is on — open the Lineups or Trade Machine page so I can take control.'
                  : 'Hi! I can answer questions about player projections. Try one of these:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {(mode === 'autopilot'
                  ? AUTOPILOT_SUGGESTIONS[activePage || ''] || []
                  : SUGGESTIONS
                ).map((s) => (
                  <Chip
                    key={s}
                    label={s}
                    size="small"
                    variant="outlined"
                    onClick={() => sendMessage(s)}
                    sx={{ fontSize: '0.72rem', color: 'secondary.main', borderColor: 'divider', cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          messages.map((m) => <MessageRow key={m.id} message={m} />)
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={mode === 'autopilot' ? 'Tell me what to do on this page…' : 'Ask about projections…'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 999 } }}
        />
        <IconButton
          onClick={submit}
          disabled={!input.trim() || sending}
          sx={{
            width: 38,
            height: 38,
            color: 'white',
            background: PINK_GRADIENT,
            '&:hover': { background: PINK_GRADIENT, filter: 'brightness(1.05)' },
            '&.Mui-disabled': { background: (t) => alpha(t.palette.primary.main, 0.35), color: 'white' },
          }}
          aria-label="Send"
        >
          <Send sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
        </>
      )}
    </Paper>
  );
};

export default AssistantWidget;
